import { Paper, Box, BoxProps, Button, ButtonProps } from "@mui/material";
import { styled, alpha } from "@mui/material/styles";
import { useTranslation } from "react-i18next";
import { Icon } from "../Icon";

export const ContentContainer = styled(Paper)(({ theme }) => ({
  background: "transparent",
  borderRadius: "0px",
  boxShadow: "none",
  height: "auto",
  maxHeight: "calc(100% - 2.5rem)",
  overflowY: "auto",
  paddingBottom: "5rem",
  paddingRight: theme.spacing(1),
  width: "100%",
  "::-webkit-scrollbar": {
    width: "0.44rem",
    backgroundColor: `${alpha(theme.customColors.exploreCardText, 0.2)}`,
    borderRadius: "0.5rem"
  },
  "&::-webkit-scrollbar-track": {
    backgroundColor: `${alpha(theme.customColors.exploreCardText, 0.2)}!important`,
    borderRadius: "0.5rem"
  },
  "&::-webkit-scrollbar-thumb": {
    backgroundColor: `${alpha(theme.customColors.exploreCardText, 1)}!important`,
    borderRadius: "0.5rem"
  }
}));

export const WidgetContainer = styled(Paper)(({ theme }) => ({
  background: "transparent",
  boxShadow: "none",
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(2),
  height: "auto",
  maxWidth: "26rem",
  width: "26rem",

  [theme.breakpoints.down("sm")]: {
    gap: theme.spacing(3),
    width: "100%"
  }
}));

export const StyledDrawerIcon = styled(Icon)(({ theme }) => ({
  "&":
    { "path":
      { fill: theme.customColors.systemSecondary05 }
    }
}));

const StyledOpenDrawerBox = styled(Box)<
    BoxProps & { accent: string }
>(({ theme }) => ({
  alignItems: "center",
  backgroundColor: theme.customColors.backgroundPrimaryCta,
  borderRadius: "0.25rem 0 0 0.25rem",
  color: theme.customColors.textPrimaryCta,
  display: "flex",
  height: "2.5rem",
  justifyContent: "center",
  opacity: 0,
  transition: "none",
  width: "2.5rem",

  "&.visible": {
    opacity: 1,
    transition: "opacity .3s linear",

    [theme.breakpoints.down("sm")]: {}
  }
}));

export const StyledOpenDrawerButton = styled(Button)<
  ButtonProps & { $top?: string }>(({ theme, $top }) => ({
    boxShadow: "10px 10px 30px rgba(0, 0, 0, 0.7)",
    minWidth: "2.5rem",
    padding: "0rem",
    position: "fixed",
    right:0,
    top: $top || "14rem",
    width: "2.5rem",
    zIndex: "1200",
    "&:focus-visible": {
      border: "1px solid white !important"
    },

    [theme.breakpoints.down("sm")]: {
      minWidth: "2.5rem",
      width: "2.5rem"
    }
  }));

export const StyledWidgetButtonDrawer = styled("div")(({ theme }) => ({
  cursor: "pointer",
  justifyContent: "space-between",
  display:"flex",
  gap:"0.5rem",
  alignItems: "center",
  background: theme.customColors.backgroundPrimaryCta,
  padding: "0.25rem 0.5rem",
  boxSizing:"content-box",
  zIndex: "9999"
}));

export const OpenDrawerButton = ({
  accent,
  onClick,
  icon,
  top,
  ...props
} : Omit<BoxProps, "children">
  & Pick<ButtonProps, "onClick">
  & {
    accent: string
    icon: string
    top?: string
  }
) => {

  const { t } = useTranslation();

  return (
    <StyledOpenDrawerButton aria-label={ t("open_widget_drawer") } tabIndex={ 0 } onClick={ onClick } title={ "sidebar" } $top={ top }>
      <StyledOpenDrawerBox accent={ accent } { ...props }>
        <StyledDrawerIcon icon={ icon } size={ 24 }/>
      </StyledOpenDrawerBox>
    </StyledOpenDrawerButton>
  );
};

export const StyledCloseDrawerBox = styled(Box)<
    BoxProps & { accent?: string }
>(({ accent, theme }) => ({
  alignItems: "center",
  backgroundColor: accent || theme.customColors.backgroundPrimaryCta,
  borderRadius: "0.25rem 0.25rem 0.25rem 0.25rem",
  color: theme.customColors.textPrimaryCta,
  display: "flex",
  height: "2.5rem",
  justifyContent: "center",
  opacity: 0,
  width: "2.5rem",

  [theme.breakpoints.down("sm")]: {
    borderRadius: "0.25rem 0.25rem 0.25rem 0.25rem",
    height: "2.5rem",
    width: "2.5rem"
  },

  "&.visible": {
    opacity: 1,
    transition: "opacity .3s linear"
  }
}));

export const StyledCloseDrawerButton = styled(Button)<
    ButtonProps & { $top?: string }>(({ theme, $top }) => ({
      borderRadius: "0.25rem 0 0 0.25rem",
      left: "0",
      minWidth: "2.5rem",
      padding: "0rem",
      position: "absolute",
      top: $top || "-3rem",
      width: "2.5rem",
      zIndex: "1200",
      "&:focus-visible": {
        border: "1px solid white !important"
      },

      [theme.breakpoints.down("sm")]: {
        left: "0"
      }

    }));

export const CloseDrawerButton = ({
  accent,
  icon,
  top,
  onClick,
  ...props
}: Omit<BoxProps, "children">
    & Pick<ButtonProps, "onClick">
    & { accent?: string, icon: string, top?: string }
) => {

  const { t } = useTranslation();

  return (
    <StyledCloseDrawerButton aria-label={ t("close_widget_drawer") } tabIndex={ 0 } onClick={ onClick } $top={ top }>
      <StyledCloseDrawerBox accent={ accent } { ...props }>
        <StyledDrawerIcon icon={ icon } size={ 24 }/>
      </StyledCloseDrawerBox>
    </StyledCloseDrawerButton>
  );
};
